// The three advisors (spec §6, plan Task 17): each persona asks the model for one named play, the play
// is re-checked against the sim's own legality rules and the event ids it was actually shown, and the
// result sits on a card until a human clicks. Nothing here touches the simulation - issuing a play is
// the store's job (src/state/advisors.ts), which is what keeps "propose, vet, then act" a property of
// the code and not just of the copy.
//
// Refresh discipline, which is what keeps the rail readable and the bill bounded:
//   - `refresh('planning')` forces all three (the planning stage opening, or the first live tick);
//   - a significant event refreshes; an insignificant one never does;
//   - otherwise at most one refresh per persona per 30 ticks (3 sim minutes), and never two at once;
//   - the three personas are asked one after another, never in parallel: each call holds a connection
//     to the same origin for up to the whole timeout, and a browser only has six of them - three at
//     once starved MapLibre of the connections it needs to fetch its worker and tiles, and the map
//     stayed black until they timed out;
//   - a template play is seeded synchronously at first sight of a context, so a play is always one
//     click away and a refresh in flight shows the previous play with a "refreshing" chip, never a
//     spinner-only card.
//
// Pitch 3 ("a staff that argues about your plan") added three things to that shape without changing it:
// the prompt is split so its stable half can actually be cached (src/ai/prompts.ts), each persona is
// shown its own previous play and the other two advisors' current ones so it can say "as before" or
// argue, and the answer is streamed so the rationale fills in under the previous play while the model
// writes rather than after it has finished.

import * as audit from './audit';
import { callLLM } from './client';
import { normalizePlayName, PlaySchema } from './schemas';
import { templatePlay } from './templates';
import { validateCitations, validatePlay, type ValidOrder } from './validate';
import {
  buildAdvisorPrompt, citableEventIds, DOCTRINE_WORD, PERSONA_IDS, personaDef,
  type AdvisorContext, type AdvisorMemory, type Persona, type PreviousPlay,
} from './prompts';
import { TICKS_PER_MIN, type Event } from '../sim/types';
import type { z } from 'zod';

export type Play = z.infer<typeof PlaySchema>;

/** One persona's current proposal, exactly as the card renders it. */
export interface AdvisorPlayState {
  persona: Persona;
  play: Play;
  source: 'llm' | 'template';
  /** Sim tick the play was built at; the card's age badge counts from here. */
  ageTick: number;
  validated: { orders: ValidOrder[]; rejected: { order: unknown; reason: string }[] };
  /** True when more than half the model's cited event ids did not exist (spec §6 "grounded narration"). */
  flagged: boolean;
  /** Why this card is showing a template, in one phrase the player can read. */
  note?: string;
  /** The raw failure behind `note`, for the tooltip: a timeout, a proxy error, a schema mismatch. */
  error?: string;
}

export type RefreshReason = Event | 'planning' | 'timer';

export interface AdvisorsDeps {
  /** The facts as they stand right now, or null when there is nothing to advise on yet. */
  getContext: () => AdvisorContext | null;
  onState: (persona: Persona, state: AdvisorPlayState) => void;
  onRefreshing: (persona: Persona, refreshing: boolean) => void;
  /**
   * The rationale of the answer being streamed, as far as it has arrived. Called many times per call
   * while the model writes; the card renders it over a dimmed copy of the previous play, which is what
   * turns a ten-second wait into something to read (pitch 3, "stream the rationale into the card").
   */
  onPartial?: (persona: Persona, rationale: string) => void;
  /** Overrides the VITE_LLM_TIMEOUT_MS default; used by the tests. */
  timeoutMs?: number;
}

/** At most one refresh per persona per 3 sim minutes (spec §6). */
export const REFRESH_GATE_TICKS = 30;

const DEFAULT_TIMEOUT_MS = 6000;

/**
 * The model timeout.
 *
 * The spec's 6 s is about how long the player waits for a play, and that question is already answered:
 * `seedTemplates` puts a standing template on every card the moment a context exists, and the model's
 * play replaces it whenever it lands. So this number is not a wait, it is the point at which an answer
 * stops being worth having — and giving up at 30 s was throwing away answers that were nearly there.
 * The three personas are called one at a time and share the browser's connection budget with the
 * report feed's own calls; measured on Sonnet with the full facts prompt, a persona lands at 20-35 s,
 * and at a 30 s cut-off a whole demo could run without a single model play reaching the rail.
 */
const TIMEOUT_FLOOR_MS = 45_000;

/**
 * How hard an advisor thinks before it answers.
 *
 * Measured on the real prompt against `claude-sonnet-5` (2026-09-21, `tools/advisor-latency.mts`):
 * at the API's default effort the model thought for 23 s before writing a character and took 27 s in
 * total, and on some calls it spent the entire 2048-token answer budget thinking and returned no text
 * at all — which this app renders, silently, as a template play. At `low` the same prompt starts
 * writing in 2.6 s and finishes in 5.3 s. That is the difference between a staff that answers you and
 * a rail that quietly gives up, and it is bought against a kind of quality the design does not rely
 * on: an advisor play is four words, one sentence and at most six orders, every one of which is
 * re-checked against the simulation's own legality rules before a human is allowed to click it.
 */
const ADVISOR_EFFORT = 'low' as const;

export function advisorTimeoutMs(): number {
  const raw = (import.meta.env?.VITE_LLM_TIMEOUT_MS ?? '') as string;
  const n = Number(raw);
  return Math.max(TIMEOUT_FLOOR_MS, Number.isFinite(n) && n > 0 ? n : DEFAULT_TIMEOUT_MS);
}

export class Advisors {
  readonly plays: Record<Persona, AdvisorPlayState | null> =
    { maneuver: null, fires: null, recon: null };

  private readonly lastRefreshTick: Record<Persona, number | null> =
    { maneuver: null, fires: null, recon: null };

  private readonly inFlight: Record<Persona, boolean> =
    { maneuver: false, fires: false, recon: false };

  private disposed = false;

  constructor(private readonly deps: AdvisorsDeps) {}

  /** Stops every in-flight result from reaching the store; the calls themselves are left to time out. */
  dispose(): void {
    this.disposed = true;
  }

  /**
   * Puts a template play on every card that has none yet, synchronously. Called the moment a context
   * exists so the rail is never empty and "execute" is always available, model or no model.
   */
  seedTemplates(): void {
    const ctx = this.deps.getContext();
    if (!ctx) return;
    for (const persona of PERSONA_IDS) {
      if (this.plays[persona]) continue;
      this.publish(this.build(persona, ctx, templatePlay(persona, ctx), 'template', {
        note: 'standing template — waiting on the first model answer',
      }));
    }
  }

  /** True when `persona` may be refreshed right now, under the gate above. */
  private due(persona: Persona, t: number, force: boolean): boolean {
    if (this.inFlight[persona]) return false;
    if (force) return true;
    const last = this.lastRefreshTick[persona];
    return last === null || t - last >= REFRESH_GATE_TICKS;
  }

  /**
   * Asks every persona that is due for a new play. Resolves when they have all answered (or fallen
   * back), so a caller can await a refresh in a test; the UI never awaits it.
   */
  async refresh(reason: RefreshReason): Promise<void> {
    if (this.disposed) return;
    // An insignificant event is not a reason to rethink the fight.
    if (typeof reason !== 'string' && !reason.significant) return;

    const ctx = this.deps.getContext();
    if (!ctx) return;

    const force = reason === 'planning';
    const due = PERSONA_IDS.filter((p) => this.due(p, ctx.t, force));
    if (due.length === 0) return;

    // Claim the whole queue up front, so a trigger that arrives while we are working through it
    // cannot start a second copy of a persona that has not had its turn yet.
    for (const persona of due) {
      this.inFlight[persona] = true;
      this.lastRefreshTick[persona] = ctx.t;
      this.deps.onRefreshing(persona, true);
    }

    // Play names claimed so far in this round, so two personas that land on the same four words are
    // told apart on the rail rather than looking like one card rendered twice.
    const claimed = new Map<string, Persona>();
    for (const persona of PERSONA_IDS) {
      const existing = this.plays[persona];
      if (existing && !due.includes(persona)) claimed.set(existing.play.name.toLowerCase(), persona);
    }
    for (const persona of due) await this.refreshOne(persona, ctx, claimed);
  }

  /** One persona's call. The caller has already claimed it; this releases it when the answer lands. */
  private async refreshOne(
    persona: Persona,
    ctx: AdvisorContext,
    claimed: Map<string, Persona> = new Map(),
  ): Promise<void> {
    try {
      const { system, user } = buildAdvisorPrompt(persona, ctx, this.memory(persona, ctx));
      let streamed = '';
      const result = await callLLM(
        {
          model: 'sonnet', system, user, schema: PlaySchema,
          // A play is a short, heavily constrained answer that a deterministic layer re-checks anyway,
          // and the player is watching a card while it is written. See `ADVISOR_EFFORT`.
          effort: ADVISOR_EFFORT,
          timeoutMs: this.deps.timeoutMs ?? advisorTimeoutMs(),
          purpose: 'advisor', auditRef: persona,
          onText: this.deps.onPartial
            ? (partial) => {
              const rationale = partialRationale(partial);
              // Only ever forward growth: a tolerant read of half-written JSON can briefly shrink
              // when an escape sequence lands mid-token, and a card whose text goes backwards reads
              // like a bug.
              if (rationale.length <= streamed.length) return;
              streamed = rationale;
              if (!this.disposed) this.deps.onPartial?.(persona, rationale);
            }
            : undefined,
        },
        () => templatePlay(persona, ctx),
      );
      if (this.disposed) return;

      // The context may have moved on while the call was out; validate against the facts the model was
      // given (citations) but the state as it is now (legality), which is what the click will hit.
      const fresh = this.deps.getContext() ?? ctx;
      const state = this.build(persona, fresh, result.value, result.source, {
        note: result.source === 'template' ? 'no usable model answer — standing template shown' : undefined,
        error: result.error,
        promptCtx: ctx,
        auditId: result.auditId,
      });
      state.play = { ...state.play, name: distinctName(persona, state.play.name, claimed) };
      claimed.set(state.play.name.toLowerCase(), persona);
      this.publish(state);
    } finally {
      this.inFlight[persona] = false;
      if (!this.disposed) this.deps.onRefreshing(persona, false);
    }
  }

  /**
   * Citation grounding, then legality. If the model's own play survives neither - every order dropped -
   * the template stands in instead, keeping the rejections visible so the player can see what the model
   * tried. A play with no orders left and no template to fall back on is still shown: "no legal play"
   * is a real answer.
   */
  private build(
    persona: Persona,
    ctx: AdvisorContext,
    play: Play,
    source: 'llm' | 'template',
    opts: {
      note?: string;
      error?: string;
      /** The context the prompt was built from, when it differs from `ctx`. */
      promptCtx?: AdvisorContext;
      /** The AI-audit row this answer came from (`src/ai/audit.ts`), to hang its validation on. */
      auditId?: number;
    } = {},
  ): AdvisorPlayState {
    const promptCtx = opts.promptCtx ?? ctx;
    // A template cites straight from the event log, so it is grounded by construction; only the model
    // is held to the narrower set of ids it was actually shown.
    const known = source === 'llm'
      ? citableEventIds(promptCtx)
      : new Set(promptCtx.recentEvents.map((e) => e.id));
    // Title Case, four words: asked for in the schema (src/ai/schemas.ts) and enforced here, because
    // three personas refreshing every few minutes is a rail whose casing otherwise visibly drifts.
    const named = { ...play, name: normalizePlayName(play.name) };
    const cited = validateCitations(named, known);
    const validated = validatePlay(cited.value, ctx.units, 0, ctx.grid, ctx.graphics);

    // What the two gates actually threw away on this call, on the record (src/ai/audit.ts). The
    // advisors validate a renamed copy of the answer, so the tag `callLLM` left on the answer object
    // does not carry over and this is recorded by hand.
    audit.note(opts.auditId, {
      citedTotal: named.cited_event_ids.length,
      citedDropped: cited.dropped,
      flagged: source === 'llm' && cited.flagged,
      ordersKept: validated.orders.length,
      ordersRejected: validated.rejected.map((r) => r.reason),
      ref: persona,
    });

    if (source === 'llm' && validated.orders.length === 0 && play.orders.length > 0) {
      const fallback = templatePlay(persona, ctx);
      const fallbackNamed = { ...fallback, name: normalizePlayName(fallback.name) };
      const fallbackCited = validateCitations(fallbackNamed, new Set(ctx.recentEvents.map((e) => e.id)));
      const fallbackValid = validatePlay(fallbackCited.value, ctx.units, 0, ctx.grid, ctx.graphics);
      return {
        persona,
        play: fallbackCited.value,
        source: 'template',
        ageTick: ctx.t,
        // Keep the model's rejections on the card: they are the reason this is a template.
        validated: { orders: fallbackValid.orders, rejected: validated.rejected },
        flagged: cited.flagged,
        note: 'every proposed order was illegal — standing template shown instead',
      };
    }

    return {
      persona,
      play: cited.value,
      source,
      ageTick: ctx.t,
      validated,
      // Only the model can be flagged for inventing evidence.
      flagged: source === 'llm' && cited.flagged,
      note: source === 'template' ? opts.note : undefined,
      error: source === 'template' ? opts.error : undefined,
    };
  }

  /**
   * What this advisor is allowed to remember: its own last play, whether the commander executed it,
   * and the two plays currently on the rail beside it.
   *
   * "Executed" is read off the order book, not off a flag this class sets: `commit` in
   * `src/state/advisors.ts` stamps every order of a bundle with the play id `persona:name`, so an
   * order carrying that id is proof the human clicked. An advisor whose advice was taken and one whose
   * advice was ignored should not say the same thing next time.
   */
  private memory(persona: Persona, ctx: AdvisorContext): AdvisorMemory {
    const mine = this.plays[persona];
    const previous: PreviousPlay | null = mine
      ? {
        name: mine.play.name,
        rationale: mine.play.rationale,
        stance: mine.play.stance,
        orders: mine.validated.orders.map((o) => ({ unitId: o.unitId, card: o.card, target: o.target })),
        executed: wasExecuted(ctx, persona, mine.play.name),
        ageMin: Math.max(0, (ctx.t - mine.ageTick) / TICKS_PER_MIN),
        fromTemplate: mine.source === 'template',
      }
      : null;

    const others = PERSONA_IDS
      .filter((p) => p !== persona)
      .flatMap((p) => {
        const state = this.plays[p];
        return state ? [{ persona: p, name: state.play.name, rationale: state.play.rationale }] : [];
      });

    return { previous, others };
  }

  private publish(state: AdvisorPlayState): void {
    if (this.disposed) return;
    this.plays[state.persona] = state;
    this.deps.onState(state.persona, state);
  }
}

/** True when an order carrying this play's id reached the order book - i.e. the commander clicked. */
function wasExecuted(ctx: AdvisorContext, persona: Persona, playName: string): boolean {
  const id = `${persona}:${playName}`;
  return ctx.orders.some((o) => o.playId === id);
}

/**
 * The `rationale` string out of a half-written JSON object.
 *
 * Structured output arrives a few characters at a time and is not parseable until the last brace, so
 * this reads the one field the card needs straight out of the partial text: everything after
 * `"rationale":"` up to the closing quote, tolerating the escape sequences that make a naive `[^"]*`
 * stop early. It is deliberately forgiving - a wrong guess costs a flicker of text that the validated
 * play overwrites a second later, while waiting for valid JSON costs the whole point of streaming.
 */
export function partialRationale(partial: string): string {
  const m = /"rationale"\s*:\s*"((?:[^"\\]|\\.)*)/.exec(partial);
  if (!m) return '';
  try {
    // Close the string and let JSON do the unescaping, so \n and \" arrive as themselves.
    return JSON.parse(`"${m[1]}"`) as string;
  } catch {
    return m[1];
  }
}

/** A play name with the collision suffix `distinctName` may have added stripped back off. */
function baseName(name: string): string {
  return name.replace(/\s*\([A-Za-z]+\)$/, '').trim().toLowerCase();
}

/** How one advisor stands relative to another right now. */
export interface AdvisorRelation {
  /** This advisor said its play cannot be run alongside that one's. */
  disagreesWith?: { persona: Persona; reason: string; from: Persona };
  /** The two plays converge: the same opening order, or the same name. */
  concursWith?: { persona: Persona; because: string };
}

/**
 * The rail read as an argument rather than as three cards.
 *
 * A disagreement is asserted by one advisor (`disagreesWith` in its answer) and shown on both cards,
 * because the commander needs to see the conflict wherever they are looking; the reason quoted on both
 * is the rationale of the advisor who raised it. A concurrence is inferred, not claimed: two plays that
 * open with the same card on the same unit, or that arrived at the same name, are agreeing whether or
 * not either of them noticed.
 */
export function relations(
  plays: Record<Persona, AdvisorPlayState | null>,
): Record<Persona, AdvisorRelation> {
  const out: Record<Persona, AdvisorRelation> = { maneuver: {}, fires: {}, recon: {} };

  for (const persona of PERSONA_IDS) {
    const state = plays[persona];
    const other = state?.play.disagreesWith;
    if (!state || !other || other === persona || !plays[other]) continue;
    const reason = state.play.rationale;
    out[persona].disagreesWith = { persona: other, reason, from: persona };
    // The counterpart carries the same dispute, named from its own side of it.
    if (!out[other].disagreesWith) out[other].disagreesWith = { persona, reason, from: persona };
  }

  for (let i = 0; i < PERSONA_IDS.length; i++) {
    for (let j = i + 1; j < PERSONA_IDS.length; j++) {
      const a = PERSONA_IDS[i], b = PERSONA_IDS[j];
      const pa = plays[a], pb = plays[b];
      if (!pa || !pb) continue;
      // A declared disagreement outranks an inferred agreement; they cannot both be shown.
      if (out[a].disagreesWith?.persona === b || out[b].disagreesWith?.persona === a) continue;
      const because = convergence(pa, pb);
      if (!because) continue;
      out[a].concursWith ??= { persona: b, because };
      out[b].concursWith ??= { persona: a, because };
    }
  }
  return out;
}

/** Why two plays are the same play, in a phrase, or null when they are not. */
function convergence(a: AdvisorPlayState, b: AdvisorPlayState): string | null {
  const first = (s: AdvisorPlayState) => s.validated.orders[0];
  const fa = first(a), fb = first(b);
  if (fa && fb && fa.unitId === fb.unitId && fa.card === fb.card) {
    return `both open with ${fa.card} on unit ${fa.unitId}`;
  }
  if (baseName(a.play.name) === baseName(b.play.name)) return 'both arrived at the same play';
  return null;
}

/** The display name of a persona, for a chip that names another advisor. */
export function personaLabel(persona: Persona): string {
  return personaDef(persona).name;
}

/**
 * The play's name, made unique across one refresh: a collision takes the persona's doctrine word.
 *
 * "Screen The North Flank" from RECON and the identical name from FIRES becomes
 * "Screen The North Flank (Mass)" on the second card, so the rail reads as two advisors agreeing
 * rather than as one card drawn twice.
 */
export function distinctName(persona: Persona, name: string, claimed: Map<string, Persona>): string {
  const owner = claimed.get(name.toLowerCase());
  if (owner === undefined || owner === persona) return name;
  const suffix = ` (${DOCTRINE_WORD[persona]})`;
  const room = 40 - suffix.length;
  return `${name.length > room ? name.slice(0, room).trimEnd() : name}${suffix}`;
}
